import React, { useEffect, useState } from 'react';

import { useGetDashboardsInfo } from "api/api-hooks/dashboards-hooks";
import { useCleanupReportHistoryRange, useDeleteReportRange } from "api/api-hooks/reports-hooks";
import { DashboardInfos } from "api/model/dashboard/DashboardInfos";
import { easasAlert } from 'components/fragments/easas-global-fragment/EasasGlobalAlerts';
import { Button } from "primereact/button";
import { T } from 'services/lang/LanguageService';
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { ProgressSpinner } from "primereact/progressspinner";
import { TemplateLoadingType } from "utils/enums";
import CalendarWrapper from "../../basic/calendar-wrapper/CalendarWrapper";
import { IPeriodLoadingConfig } from "../product-template-table/utils/interfaces";
import { getTemplateLoadingRange } from "../product-template-table/utils/timeUtils";

type RemoveReportRangeType = {
    ids: Array<number>
    loadingConfig?: IPeriodLoadingConfig
    function: "REMOVE" | "CLEANUP"
    close(): void
}

const RemoveReportRange = (props: RemoveReportRangeType) => {
    const [dashboard, setDashboard] = useState<DashboardInfos | undefined>(undefined);

    const [fromDate, setFromDate] = useState<Date | undefined>();
    const [toDate, setToDate] = useState<Date | undefined>();

    const {data, isLoading} = useGetDashboardsInfo(props.ids);

    const {mutate: cleanupRange} = useCleanupReportHistoryRange({
        onSuccess: () => {
            easasAlert({
              summary: T("alert.report-table.successfully-cleaned-up-report-history-range"),
              severity: "success",
            });
        },
        onFailure: () => {
            easasAlert({
              summary: T("alert.report-table.an-error-occurred-while-cleaning-up-report-history-range"),
              severity: "error",
            });
        },
        onMutate: () => {
            easasAlert(T("alert.report-table.initiating-cleanup-of-report-history-range"));
            props.close()
        }
    })

    const {mutate: removeRange} = useDeleteReportRange({
        onSuccess: () => {
            easasAlert({ summary: T("alert.report-table.successfully-removed-report-range"), severity: "success" });
        },
        onFailure: () => {
            easasAlert({
              summary: T("alert.report-table.an-error-occurred-while-removing-report-range"),
              severity: "error",
            });
        },
        onMutate: () => {
            easasAlert(T("alert.report-table.initiating-removal-of-report-range"));
            props.close()
        }
    })

    useEffect(() => {
        const {start, end} = getTemplateLoadingRange({
            loadingType: TemplateLoadingType.RANGE,
            loadingConfig: props.loadingConfig
        });

        setFromDate(start || new Date());
        setToDate(end || new Date());
    }, [])

    useEffect(() => {
        if (data && !isLoading && data.length === 1) {
            setDashboard(data[0])
        }
    }, [data, isLoading])

    const onDashboardChange = (e: { value: any }) => {
        setDashboard(e.value);
    }

    const onButtonClick = () => {
        if (props.function === "REMOVE") {
            onRemoveRangeClick()
        } else if (props.function === "CLEANUP") {
            onCleanupHistoryRangeClick()
        }
    }

    const onRemoveRangeClick = () => {
        if (!dashboard || !fromDate || !toDate) {
            easasAlert({ summary: T("alert.warning"), detail: T("alert.report-table.no-dashboard-selected"), severity: "warn" });
            return
        }

        removeRange({dashboardId: dashboard.id, from: fromDate.toISOString(), to: toDate.toISOString()})
        props.close()
    }

    const onCleanupHistoryRangeClick = () => {
        if (!dashboard || !fromDate || !toDate) {
            easasAlert({ summary: T("alert.warning"), detail: T("alert.report-table.no-dashboard-selected"), severity: "warn" });
            return
        }

        cleanupRange({dashboardId: dashboard.id, params: {from: fromDate.toISOString(), to: toDate.toISOString()}})
        props.close()
    }

    if (isLoading) {
        return <ProgressSpinner/>
    }

    return (
      <div className="flex flex-column gap-3">
        <div>
          {props.function === "REMOVE" ? (
            <>
              {T("message.report-table.permanently-erase-all-report-generations-from")} <br /> {T("message.report-table.the-dashboard")}
            </>
          ) : (
            <>
              {T("message.report-table.deletes-the-dashboard-report-generation-history")}
              <br /> {T("message.report-table.preserves-only-the-latest-report")}
            </>
          )}
        </div>

        <div className="flex flex-column">
          <label htmlFor="report">
            <b>{T("word.dashboard")}</b>
          </label>
          {props.ids.length > 1 && props.ids.length !== 0 ? (
            <Dropdown
              value={dashboard}
              options={data}
              onChange={onDashboardChange}
              optionLabel="title"
              placeholder={T("action.select-a-dashboard")}
            />
          ) : (
            <InputText value={dashboard?.title || ""} disabled />
          )}
        </div>

        <div className="flex flex-column gap-1">
          <div style={{ fontWeight: "bold", fontSize: "larger", paddingTop: "5px" }}>
            {T("load-conf.reference-time")}
          </div>
          <div className="flex flex-column">
            <label htmlFor="from">
              <b>{T("word.from")}</b>
            </label>
            <CalendarWrapper id="from" value={fromDate || new Date()} onChange={setFromDate} />
          </div>
          <div className="flex flex-column">
            <label htmlFor="to">
            <b>{T("word.to")}</b>
            </label>
            <CalendarWrapper id="to" value={toDate || new Date()} onChange={setToDate} />
          </div>
        </div>

        <Button label={T("action.proceed")} onClick={() => onButtonClick()} disabled={!dashboard} />
      </div>
    );
};

export default RemoveReportRange;